import { useEffect, useRef, useState } from "react";

import {
	BulkSelectionItemMutation,
	BulkSelectionMutation,
	BulkSelectionQuery,
	useApplyBulkUpdateMutation,
	useBulkSelectionMutation,
	useBulkSelectionObject,
	useBulkSelectionObjectCreationMutation,
} from "../../api/hooks";

import { ISelectionObject } from "../../api/services";

import { useFeatureFlagQuery } from "../useFeatureFlag";
import useToast from "../useToast";

export const GOAL_TYPE = "goal";

/*
	This custom hook is used to manage bulk selection of items in the UI.
	It is used to manage the state of the bulk selection object in the backend.
	
	* When the user selects items in the UI, they are added cached while a bulk selection object is made.
	  Once it's made, we send the cached items to the backend to be added to the bulk selection object.

	* When the user removes items from the UI, we remove them from the cached items and send the updated list to the backend.
	* The same processes are used for quries.

	* Bulk updating is done by sending the bulk selection object ID to the backend and applying the bulk update to the items in the bulk selection object.

	* This hook uses optimistic updating to update the UI before the backend responds when adding/removing items from the bulk selection object.
*/

type BulkSelectionOptions = {
	onBulkUpdateSuccess?: () => void;
};

export const useBulkSelection = (type: string, options?: BulkSelectionOptions) => {
	const toast = useToast();
	const [selectionObjectID, setSelectionObjectID] = useState<string>("");
	const [selections, setSelections] = useState<string[]>([]);
	const [isQuerying, setIsQuerying] = useState<boolean>(false);
	const { value: shouldUseInternalApiForArchirve } = useFeatureFlagQuery("ff-temp-archive-goals-internal-api", false);

	const cachedInitialSelections = useRef<string[]>([]);
	const cachedQuery = useRef<BulkSelectionQuery[]>([]);

	const refetchTimeout = useRef<null | ReturnType<typeof setTimeout>>(null);

	// This function handles rolling back data on the local copy when a response has an error
	const onErrorRollbackSelections = (_error, bulkSelectionMutationObject: BulkSelectionMutation) => {
		const { object_ids: items, action } = bulkSelectionMutationObject as BulkSelectionItemMutation;
		let rollbackItems: string[] = [];
		if (action === "add") {
			if (items) {
				rollbackItems = selections.filter((item) => !items.includes(item));
			} else {
				rollbackItems = [...selections];
			}
		} else if (items) {
			rollbackItems = [...selections, ...items];
		}
		setSelections([...rollbackItems]);

		toast(`Error ${bulkSelectionMutationObject?.action === "add" ? "Adding To" : "Removing From"} Bulk Selection`, {
			type: "error",
			duration: 2000,
			size: "small",
		});
	};

	// this function is called when the backend responds with a success when adding/removing items from the bulk selection object
	const onSuccess = () => {
		refetch();
	};

	// This hook is used to get the bulk selection object from the backend
	const {
		data: selectionObjectResponse,
		isLoading,
		refetch,
		isRefetching,
	} = useBulkSelectionObject(selectionObjectID);

	// This hook is used to add/remove items from the bulk selection object
	const { mutate: mutateSelections, isPending: isMutationLoading } = useBulkSelectionMutation(
		selectionObjectID,
		onErrorRollbackSelections,
		onSuccess
	);

	// This hook is used to create a new bulk selection object
	const { mutate: createNewSelectionObject, isPending: isCreationLoading } = useBulkSelectionObjectCreationMutation(
		type,
		(data: ISelectionObject) => {
			setSelectionObjectID(data.result.bulk_selection_id);
		},
		() => {
			toast("Error Initializing Bulk Selection", {
				type: "error",
				duration: 5000,
				size: "small",
			});
			cachedInitialSelections.current = [];
			setSelections([]);
		}
	);

	// This hook is used to apply a bulk update to the items in the bulk selection object
	const { mutate: applyBulkUpdateMutation, isPending: isApplyingBulkUpdate } = useApplyBulkUpdateMutation(
		selectionObjectID,
		type,
		() => {
			if (options?.onBulkUpdateSuccess) {
				options?.onBulkUpdateSuccess();
				clearAll();
			}
		},
		() => {
			toast("Error Updating Bulk Selection", {
				type: "error",
				duration: 5000,
				size: "small",
			});
			cachedInitialSelections.current = [];
			setSelections([]);
		},
		shouldUseInternalApiForArchirve
	);

	// This hook is used to initialize the bulk selection object after it's created
	// We're adding cached items/queries to the bulk selection object after it's created
	useEffect(() => {
		if (selectionObjectID) {
			if (cachedInitialSelections.current.length) {
				const initialSelections = [...cachedInitialSelections.current];
				cachedInitialSelections.current = [];
				mutateSelections({ object_ids: initialSelections, action: "add" });
			}

			if (cachedQuery.current.length) {
				const initialQuery = [...cachedQuery.current];
				cachedQuery.current = [];
				mutateSelections({ query: initialQuery, action: "add" });
				setIsQuerying(true);
			}
		}
	}, [selectionObjectID]);

	// This hook is used to update the local copy of the bulk selection object when the backend responds with a success, Here we're keeping the local copy true
	useEffect(() => {
		if (isQuerying && !isRefetching && !isLoading && selectionObjectID && !isMutationLoading) {
			setSelections(selectionObjectResponse?.result?.bulk_selection?.object_ids || selections);
			setIsQuerying(false);
		}
	}, [isQuerying, isRefetching, isLoading, selectionObjectID, isMutationLoading]);

	const addItemsToSelection = async (items: string[]) => {
		if (selectionObjectID) {
			setSelections([...selections, ...items]);
			mutateSelections({ object_ids: items, action: "add" });
		} else {
			cachedInitialSelections.current = cachedInitialSelections.current.concat(items);
			setSelections(cachedInitialSelections.current);
			if (!isCreationLoading) {
				createNewSelectionObject({ object_ids: [] });
			}
		}
	};

	useEffect(() => {
		// clear on component unmount
		return () => {
			if (refetchTimeout.current !== null) {
				clearTimeout(refetchTimeout.current);
			}
		};
	}, []);

	const removeItemsFromSelection = async (items: string[]) => {
		if (selectionObjectID) {
			const filteredSelections = selections.filter((item) => !items.includes(item));

			setSelections([...filteredSelections]);
			mutateSelections({ object_ids: items, action: "remove" });
		} else {
			cachedInitialSelections.current = cachedInitialSelections.current.filter((item) => !items.includes(item));
			setSelections(cachedInitialSelections.current);
			if (!isCreationLoading) {
				createNewSelectionObject({ object_ids: [] });
			}
		}
	};

	const addQueryToSelection = async (query: BulkSelectionQuery[] = []) => {
		if (selectionObjectID) {
			setIsQuerying(true);

			mutateSelections({ query, action: "add" });
		} else {
			cachedQuery.current = query;
			if (!isCreationLoading) {
				createNewSelectionObject({ query: cachedQuery.current });
			}
		}
	};

	const removeQueryFromSelection = async (query: BulkSelectionQuery[] = []) => {
		if (selectionObjectID) {
			mutateSelections({ query, action: "remove" });
		} else {
			cachedQuery.current = cachedQuery.current.filter((item) => !query.includes(item));
			if (!isCreationLoading) {
				createNewSelectionObject({ query: cachedQuery.current });
			}
		}
	};

	const clearAll = () => {
		setSelections([]);
		cachedInitialSelections.current = [];
		setSelectionObjectID("");
	};

	const applyBulkUpdate = async (bulkUpdateObject) => {
		if (selectionObjectID) {
			applyBulkUpdateMutation(bulkUpdateObject);
		}
	};

	return {
		selections: selections.filter((item, index) => selections.indexOf(item) === index),
		refetch,
		add: addItemsToSelection,
		remove: removeItemsFromSelection,
		addQuery: addQueryToSelection,
		removeQuery: removeQueryFromSelection,
		clearAll: clearAll,
		applyBulkUpdate,
		isQuerying,
		isMutationLoading,
		isCreationLoading,
		isBulkSelecitonObjectRefetching: isRefetching,
		isBulkSelectionObjectLoading: isLoading,
		isApplyingBulkUpdate,
		isInitialized: !!selectionObjectID,
	};
};
